import { useId, useMemo, useState, type ReactNode } from "react";
import { docT } from "./doc-locale";
import "./doc-calc.css";

// The webinar yield calculator (F95, Pavel 2026-08-07): "zadám cíl, kolik chci
// prodat kusů a kolik mi to vydělá... měly by tam být předvyplněné obvyklé
// konverze, ale měl bych možnost si to počítat jako kalkulačku, abych měl
// představu, kolik se reálně prodá."
//
// The design decision the whole file rests on: this is ONE chain, drivable
// from EITHER end, never two calculators. Forward the owner types how many
// people arrive and reads the money; from the goal she types the money (or the
// number of orders) and reads how many people she has to bring. Every element
// keeps its place in both directions - only which end is editable changes and
// the connectors turn round - so the reader learns the object once.
//
// Three laws it shares with the rest of the document language:
//  - Nothing is injected. `<calc>` carries DATA (audiences, rates, tariffs);
//    every behaviour is written here.
//  - Every affordance answers hover, keyboard AND tap. The switches are real
//    radio inputs, so arrows move between them without a line of JS.
//  - A control that cannot do its job is not drawn as a control. A calculator
//    with no audiences renders as its prose; with no tariff the money band and
//    the money goal are absent instead of showing a made-up price.
//
// And one law of its own: NEVER A SINGLE NUMBER. The benchmarks are ranges, so
// every result is a range, with the middle offered as the value to plan with.

/* ---------------------------------------------------------------- numbers -- */

export interface CalcRange {
  min: number;
  max: number;
}

/**
 * `50-70`, `50 az 70`, `50` -> a range. Tolerant on purpose: the source is
 * model output, and a rate written as one number is a legitimate "we know this
 * one exactly" rather than a defect.
 */
export function parseCalcRange(raw: string | undefined): CalcRange | null {
  const numbers = (raw ?? "").match(/\d+(?:[.,]\d+)?/g);
  if (!numbers || numbers.length === 0) return null;
  const values = numbers.slice(0, 2).map((value) => Number(value.replace(",", ".")));
  const min = Math.min(...values);
  const max = Math.max(...values);
  return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : null;
}

/**
 * `1234567` -> `1 234 567` with non-breaking spaces.
 *
 * Written out rather than delegated to `Intl.NumberFormat`: the group
 * separator ICU returns for cs-CZ changed between versions (space, then narrow
 * no-break space), and the SSR fixture, the guards and the browser must print
 * the SAME string or a copy button pastes something the reader never saw.
 *
 * `decimals` exists for ONE value: how many times the ad money comes back.
 * Everything else here is people or whole crowns, where a decimal is noise -
 * but rounding a return of 0.89x up to "1x" tells the owner she broke even on
 * the very run where the line above it says she lost money.
 */
export function calcNumber(value: number, decimals = 0): string {
  if (!Number.isFinite(value)) return "";
  const factor = 10 ** decimals;
  const rounded = Math.round(value * factor) / factor;
  const sign = rounded < 0 ? "-" : "";
  const size = Math.abs(rounded);
  const digits = String(Math.floor(size));
  let out = "";
  for (let index = 0; index < digits.length; index += 1) {
    if (index > 0 && (digits.length - index) % 3 === 0) out += " ";
    out += digits[index];
  }
  if (decimals <= 0) return `${sign}${out}`;
  const fraction = String(Math.round((size - Math.floor(size)) * factor)).padStart(decimals, "0");
  return `${sign}${out}${docT("doc.calc.decimalSep")}${fraction}`;
}

/** A result: the pessimistic end, the optimistic end, and the middle to plan with. */
interface Band {
  lo: number;
  hi: number;
  mid: number;
}

const band = (lo: number, hi: number, mid: number): Band => ({ lo, hi, mid });
const usable = (value: Band): boolean => [value.lo, value.hi, value.mid].every(Number.isFinite);
const midOf = (range: CalcRange): number => (range.min + range.max) / 2;

/* ------------------------------------------------------------------ types -- */

export interface DocCalcAudience {
  /** Document-owned key (`znaji` / `cizi`); never shown to the reader. */
  key: string;
  /** The owner's own words for this audience. Never "cold"/"warm". */
  name: string;
  /** One sentence under the switch: what changed by picking this. */
  note: string;
  /** Visits of the registration page that turn into registrations, in %. */
  reg: CalcRange;
  /** Registrations that show up for the broadcast, in %. */
  show: CalcRange;
  /** 🔴 Of those who SHOWED UP, how many buy, in %. Never of the registered. */
  buy: CalcRange;
  /** Whether this audience is paid for: the ad money band lives on it. */
  ads: boolean;
  /** Cost per visit of the registration page, in the document's currency. */
  cpv: number;
}

export interface DocCalcTariff {
  name: string;
  price: number;
  recommended: boolean;
}

/* -------------------------------------------------------------- fragments -- */

/**
 * A segmented switch built from real radio inputs: arrow keys move between the
 * options, Space selects, focus is visible, and a screen reader hears a group
 * with a legend. A row of `<button>`s would need all of that written by hand.
 */
function CalcSwitch(props: {
  name: string;
  legend: string;
  value: string;
  options: Array<{ key: string; label: string }>;
  onPick: (key: string) => void;
}) {
  return (
    <fieldset className="doc-calc-switch">
      <legend className="doc-calc-switch-legend">{props.legend}</legend>
      <div className="doc-calc-switch-row">
        {props.options.map((option) => (
          <span className="doc-calc-switch-cell" key={option.key}>
            <input
              type="radio"
              id={`${props.name}-${option.key}`}
              name={props.name}
              className="doc-calc-radio"
              checked={props.value === option.key}
              onChange={() => props.onPick(option.key)}
            />
            <label htmlFor={`${props.name}-${option.key}`} className="doc-calc-switch-label">
              {option.label}
            </label>
          </span>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * An editable number.
 *
 * Two-digit rates keep `type="number"`, so the arrow keys step the model - the
 * fastest way to ask "and if it were three points worse". Counts and money do
 * NOT: a six-digit goal printed as `250320` beside results printed as
 * `250 320 Kč` is the one ungrouped number on the block, and grouping is
 * impossible inside a numeric input. They are text fields that show the grouped
 * value at rest and the raw digits while they are being typed into.
 */
function CalcField(props: {
  id: string;
  label: string;
  value: string;
  suffix?: string;
  size: "pct" | "count" | "money";
  onChange: (value: string) => void;
  /**
   * Fired when the field is left. The goal pair uses it to SETTLE on a
   * reachable number: nobody sells 33.5 subscriptions, so a money goal typed
   * as 300 000 has to come to rest on the amount 34 orders really make. While
   * the field has focus it keeps exactly what is being typed - snapping every
   * keystroke would fight the person typing.
   */
  onCommit?: (value: string) => void;
}) {
  const [typing, setTyping] = useState(false);
  const stepped = props.size === "pct";
  const shown = stepped || typing ? props.value : calcNumber(Number(props.value.replace(/\s/g, "")) || 0);
  return (
    <span className={`doc-calc-field doc-calc-field-${props.size}`}>
      <input
        id={props.id}
        type={stepped ? "number" : "text"}
        min={stepped ? "0" : undefined}
        inputMode="decimal"
        className="doc-calc-input"
        aria-label={props.label}
        value={shown}
        onChange={(event) => props.onChange(event.target.value.replace(/\s/g, ""))}
        onFocus={(event) => { setTyping(true); event.target.select(); }}
        onBlur={(event) => {
          setTyping(false);
          props.onCommit?.(event.target.value.replace(/\s/g, ""));
        }}
      />
      {props.suffix && <span className="doc-calc-unit" aria-hidden="true">{props.suffix}</span>}
    </span>
  );
}

/**
 * A result. The RANGE is the value (Pavel: "nikdy nepředstírej přesnost, kterou
 * data nemají"); the middle is offered underneath as the number to plan with,
 * and only where a decision is made - on the answering end of the chain and on
 * the money, never on every intermediate row, which would be a wall of digits.
 */
function CalcValue(props: {
  value: Band; unit?: string; showMid?: boolean; tone?: "lead" | "money"; decimals?: number;
}) {
  if (!usable(props.value)) {
    return <span className="doc-calc-value is-empty">{docT("doc.calc.needsRate")}</span>;
  }
  const show = (value: number) => calcNumber(value, props.decimals ?? 0);
  // Compared as PRINTED, not as computed: two ends that round to the same
  // string must not be drawn as a range, or the row reads "7 az 7".
  const single = show(props.value.lo) === show(props.value.hi);
  const tone = props.tone ? ` is-${props.tone}` : "";
  return (
    <span className={`doc-calc-value${tone}`} aria-live="polite">
      <span className="doc-calc-range">
        <span className="doc-calc-num">{show(props.value.lo)}</span>
        {!single && (
          <>
            <span className="doc-calc-sep">{docT("doc.calc.rangeSep")}</span>
            <span className="doc-calc-num">{show(props.value.hi)}</span>
          </>
        )}
        {props.unit && <span className="doc-calc-unit">{props.unit}</span>}
      </span>
      {!single && props.showMid && (
        <span className="doc-calc-mid">
          {docT("doc.calc.midHint")} {show(props.value.mid)}
          {props.unit ? ` ${props.unit}` : ""}
        </span>
      )}
    </span>
  );
}

/* ----------------------------------------------------------------- block --- */

export function DocCalc(props: {
  title?: string;
  /** Visits of the registration page the forward direction starts from. */
  visits: number;
  currency: string;
  audiences: DocCalcAudience[];
  tariffs: DocCalcTariff[];
  children?: ReactNode;
}) {
  const uid = useId();
  const [audKey, setAudKey] = useState(props.audiences[0]?.key ?? "");
  const [goalMode, setGoalMode] = useState(false);
  const [tariffIndex, setTariffIndex] = useState(
    Math.max(0, props.tariffs.findIndex((tariff) => tariff.recommended))
  );
  // ONE override map for every editable number, so "back to the prefilled
  // numbers" is `{}` and an edit made under one audience survives a look at
  // the other one (Pavel asked the switch to change every number; he did not
  // ask it to throw the owner's own numbers away).
  const [edits, setEdits] = useState<Record<string, string>>({});
  const set = (key: string, value: string) => setEdits((current) => ({ ...current, [key]: value }));

  const aud = props.audiences.find((entry) => entry.key === audKey) ?? props.audiences[0];
  const tariff = props.tariffs[tariffIndex] ?? props.tariffs[0];
  const price = tariff?.price ?? 0;

  const num = (value: string): number => {
    const parsed = Number(String(value).replace(",", ".").replace(/\s/g, ""));
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  };
  const rateKey = (name: string, bound: "min" | "max") => `${aud?.key ?? ""}.${name}.${bound}`;
  const rateText = (name: "reg" | "show" | "buy", bound: "min" | "max"): string =>
    edits[rateKey(name, bound)] ?? String(aud ? aud[name][bound] : 0);
  const rateOf = (name: "reg" | "show" | "buy"): CalcRange => {
    const lo = num(rateText(name, "min")) / 100;
    const hi = num(rateText(name, "max")) / 100;
    return { min: Math.min(lo, hi), max: Math.max(lo, hi) };
  };

  const visitsText = edits.visits ?? String(props.visits);
  const cpvText = edits[`${aud?.key ?? ""}.cpv`] ?? String(aud?.cpv ?? 0);

  // The goal in orders, seeded from the middle of the prefilled forward run so
  // the other direction opens on a believable number instead of zero. The goal
  // belongs to the OWNER, not to an audience: switching who comes must not
  // change what she wants to sell.
  const seedGoal = props.audiences[0]
    ? Math.max(
      1,
      Math.round(
        props.visits * midOf(props.audiences[0].reg) * midOf(props.audiences[0].show)
        * midOf(props.audiences[0].buy) / 1e6
      )
    )
    : 0;
  const goalUnitsText = edits["goal.units"] ?? String(seedGoal);
  const goalUnits = num(goalUnitsText);
  const goalMoneyText = edits["goal.money"] ?? String(Math.round(goalUnits * price));

  const chain = useMemo(() => {
    if (!aud) return null;
    const reg = rateOf("reg");
    const show = rateOf("show");
    const buy = rateOf("buy");
    // 🔴 Every node is WHOLE PEOPLE, and the rounding happens here rather than
    // in the printing, so what the owner reads is what the next row was
    // computed from. Left unrounded, 12.5 orders printed as "13" and earned
    // 111 750 Kc instead of the 116 220 Kc her own calculator gives for 13 x
    // 8 940 - a chain that does not add up is a chain she stops believing.
    const whole = (value: Band): Band =>
      band(Math.round(value.lo), Math.round(value.hi), Math.round(value.mid));
    const step = (value: Band, rate: CalcRange): Band =>
      whole(band(value.lo * rate.min, value.hi * rate.max, value.mid * midOf(rate)));
    // Backwards the ranges swap ends on their own: the pessimistic rate is the
    // one that demands the MOST people, so `lo` stays the optimistic end and
    // the reader never has to think about which way the range points.
    const back = (value: Band, rate: CalcRange): Band =>
      whole(band(value.lo / rate.max, value.hi / rate.min, value.mid / midOf(rate)));

    if (!goalMode) {
      const visits = Math.round(num(visitsText));
      const seed = band(visits, visits, visits);
      const registrations = step(seed, reg);
      const attended = step(registrations, show);
      return { visits: seed, registrations, attended, orders: step(attended, buy) };
    }
    const target = Math.round(goalUnits);
    const orders = band(target, target, target);
    const attended = back(orders, buy);
    const registrations = back(attended, show);
    return { visits: back(registrations, reg), registrations, attended, orders };
    // `rateOf`/`num` are pure reads of `aud` + `edits`, both listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aud, edits, goalMode, goalUnits, visitsText]);

  const money = useMemo(() => {
    if (!chain || !tariff) return null;
    const revenue = band(chain.orders.lo * price, chain.orders.hi * price, chain.orders.mid * price);
    if (!aud?.ads) return { revenue, cost: null, net: null, times: null };
    const cpv = num(cpvText);
    const cost = band(chain.visits.lo * cpv, chain.visits.hi * cpv, chain.visits.mid * cpv);
    // One formula for both directions: forward the cost is fixed and the
    // revenue moves, from the goal the revenue is fixed and the cost moves.
    // Pairing the pessimistic revenue with the highest cost is right in both.
    const net = band(revenue.lo - cost.hi, revenue.hi - cost.lo, revenue.mid - cost.mid);
    const times = band(
      cost.hi > 0 ? revenue.lo / cost.hi : NaN,
      cost.lo > 0 ? revenue.hi / cost.lo : NaN,
      cost.mid > 0 ? revenue.mid / cost.mid : NaN
    );
    return { revenue, cost, net, times };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chain, tariff, price, aud, cpvText]);

  // No audience means no rates: there is nothing to compute and a calculator
  // drawn over nothing would be the dead UI this document language forbids.
  if (!aud || !chain) {
    return props.children ? <div className="doc-calc-body">{props.children}</div> : null;
  }

  // Both goal fields come to rest on the same whole number of orders, written
  // from ONE place so they can never settle on different truths.
  const settleGoal = (units: number) => {
    const whole = Math.max(0, Number.isFinite(units) ? units : 0);
    setEdits((current) => ({
      ...current,
      "goal.units": String(whole),
      "goal.money": String(whole * price)
    }));
  };

  const dirty = Object.keys(edits).length > 0;
  const driverLabel = docT("doc.calc.driver");
  const answerLabel = docT("doc.calc.answer");

  const rateRow = (name: "reg" | "show" | "buy", label: string, base: string) => (
    <li className="doc-calc-link">
      <span className="doc-calc-arrow" aria-hidden="true" />
      <span className="doc-calc-link-main">
        <span className="doc-calc-link-label">{label}</span>
        <span className="doc-calc-link-fields">
          <CalcField
            id={`${uid}-${name}-min`}
            label={`${label} ${docT("doc.calc.from")}`}
            value={rateText(name, "min")}
            size="pct"
            onChange={(value) => set(rateKey(name, "min"), value)}
          />
          <span className="doc-calc-sep">{docT("doc.calc.rangeSep")}</span>
          <CalcField
            id={`${uid}-${name}-max`}
            label={`${label} ${docT("doc.calc.to")}`}
            value={rateText(name, "max")}
            size="pct"
            suffix="%"
            onChange={(value) => set(rateKey(name, "max"), value)}
          />
        </span>
        <span className="doc-calc-link-base">{base}</span>
      </span>
    </li>
  );

  return (
    <section className="doc-calc" aria-label={props.title || docT("doc.calc.aria")}>
      <header className="doc-calc-head">
        <p className="doc-calc-title">{props.title || docT("doc.calc.aria")}</p>
        {dirty && (
          <button type="button" className="doc-calc-reset" onClick={() => setEdits({})}>
            {docT("doc.calc.reset")}
          </button>
        )}
      </header>

      <div className="doc-calc-switches">
        <CalcSwitch
          name={`${uid}-aud`}
          legend={docT("doc.calc.audienceLegend")}
          value={aud.key}
          options={props.audiences.map((entry) => ({ key: entry.key, label: entry.name }))}
          onPick={setAudKey}
        />
        <CalcSwitch
          name={`${uid}-mode`}
          legend={docT("doc.calc.modeLegend")}
          value={goalMode ? "goal" : "forward"}
          options={[
            { key: "forward", label: docT("doc.calc.modeForward") },
            { key: "goal", label: docT("doc.calc.modeGoal") }
          ]}
          onPick={(key) => setGoalMode(key === "goal")}
        />
      </div>
      {aud.note && <p className="doc-calc-note">{aud.note}</p>}

      <ol className={`doc-calc-chain${goalMode ? " is-goal" : ""}`}>
        <li className={`doc-calc-node${goalMode ? " is-answer" : " is-driver"}`}>
          <span className="doc-calc-node-main">
            <span className="doc-calc-role">{goalMode ? answerLabel : driverLabel}</span>
            <span className="doc-calc-node-label">
              {goalMode ? docT("doc.calc.visitsNeeded") : docT("doc.calc.visits")}
            </span>
          </span>
          {goalMode ? (
            <CalcValue value={chain.visits} showMid tone="lead" />
          ) : (
            <CalcField
              id={`${uid}-visits`}
              label={docT("doc.calc.visits")}
              value={visitsText}
              size="count"
              onChange={(value) => set("visits", value)}
            />
          )}
        </li>

        {rateRow("reg", docT("doc.calc.rateReg"), docT("doc.calc.baseVisits"))}
        <li className="doc-calc-node">
          <span className="doc-calc-node-main">
            <span className="doc-calc-node-label">{docT("doc.calc.registrations")}</span>
          </span>
          <CalcValue value={chain.registrations} />
        </li>

        {rateRow("show", docT("doc.calc.rateShow"), docT("doc.calc.baseRegistrations"))}
        <li className="doc-calc-node">
          <span className="doc-calc-node-main">
            <span className="doc-calc-node-label">{docT("doc.calc.attended")}</span>
          </span>
          <CalcValue value={chain.attended} />
        </li>

        {/* 🔴 The base is written into the row, not left to the reader: Pavel
            2026-08-07 named the purchase rate as a share of those who CAME. */}
        {rateRow("buy", docT("doc.calc.rateBuy"), docT("doc.calc.baseAttended"))}
        <li className={`doc-calc-node${goalMode ? " is-driver" : ""}`}>
          <span className="doc-calc-node-main">
            {goalMode && <span className="doc-calc-role">{driverLabel}</span>}
            <span className="doc-calc-node-label">{docT("doc.calc.orders")}</span>
          </span>
          {goalMode ? (
            <span className="doc-calc-goal">
              {/* The two goal fields are ONE number in two units, so both
                  settle on the same whole order count when they are left.
                  Without the settle, a money goal of 300 000 Kc sat beside
                  34 orders and a revenue of 303 960 Kc: three numbers, two
                  of which disagree, in the block whose job is to add up. */}
              <CalcField
                id={`${uid}-goal-units`}
                label={docT("doc.calc.goalUnits")}
                value={goalUnitsText}
                size="count"
                onChange={(value) => {
                  set("goal.units", value);
                  set("goal.money", String(Math.round(num(value) * price)));
                }}
                onCommit={(value) => settleGoal(Math.round(num(value)))}
              />
              {price > 0 && (
                <>
                  <span className="doc-calc-goal-eq" aria-hidden="true">=</span>
                  <CalcField
                    id={`${uid}-goal-money`}
                    label={docT("doc.calc.goalMoney")}
                    value={goalMoneyText}
                    size="money"
                    suffix={props.currency}
                    onChange={(value) => {
                      set("goal.money", value);
                      set("goal.units", String(Math.max(0, Math.round(num(value) / price))));
                    }}
                    onCommit={(value) => settleGoal(Math.round(num(value) / price))}
                  />
                </>
              )}
            </span>
          ) : (
            <CalcValue value={chain.orders} showMid />
          )}
        </li>
      </ol>

      {money && tariff && (
        <div className="doc-calc-money">
          {props.tariffs.length > 1 && (
            <fieldset className="doc-calc-tariffs">
              <legend className="doc-calc-switch-legend">{docT("doc.calc.tariffLegend")}</legend>
              <div className="doc-calc-switch-row">
                {props.tariffs.map((entry, index) => (
                  <span className="doc-calc-switch-cell" key={`${entry.name}-${index}`}>
                    <input
                      type="radio"
                      id={`${uid}-tariff-${index}`}
                      name={`${uid}-tariff`}
                      className="doc-calc-radio"
                      checked={tariffIndex === index}
                      onChange={() => setTariffIndex(index)}
                    />
                    <label htmlFor={`${uid}-tariff-${index}`} className="doc-calc-switch-label">
                      {entry.name} <b>{calcNumber(entry.price)} {props.currency}</b>
                      {entry.recommended && <i className="doc-calc-tariff-mark">{docT("doc.calc.recommended")}</i>}
                    </label>
                  </span>
                ))}
              </div>
            </fieldset>
          )}
          {props.tariffs.length === 1 && (
            <p className="doc-calc-note">
              {docT("doc.calc.tariffSingle")} {tariff.name}, {calcNumber(tariff.price)} {props.currency}
            </p>
          )}

          <p className="doc-calc-money-label">{docT("doc.calc.revenue")}</p>
          <CalcValue value={money.revenue} unit={props.currency} showMid tone="money" />

          {money.cost && money.net && money.times && (
            <div className="doc-calc-ads">
              <p className="doc-calc-ads-row">
                <label className="doc-calc-ads-label" htmlFor={`${uid}-cpv`}>{docT("doc.calc.cpv")}</label>
                <CalcField
                  id={`${uid}-cpv`}
                  label={docT("doc.calc.cpv")}
                  value={cpvText}
                  size="money"
                  suffix={props.currency}
                  onChange={(value) => set(`${aud.key}.cpv`, value)}
                />
              </p>
              <p className="doc-calc-ads-row">
                <span className="doc-calc-ads-label">{docT("doc.calc.budget")}</span>
                <CalcValue value={money.cost} unit={props.currency} />
              </p>
              <p className="doc-calc-ads-row is-net">
                <span className="doc-calc-ads-label">{docT("doc.calc.net")}</span>
                <CalcValue value={money.net} unit={props.currency} showMid />
              </p>
              <p className="doc-calc-ads-row">
                <span className="doc-calc-ads-label">{docT("doc.calc.times")}</span>
                {/* 🔴 One decimal, alone in this block: a return of 0.9x has
                    to read as less than one, or it contradicts the loss
                    printed on the line above. */}
                <CalcValue value={money.times} unit={docT("doc.calc.timesUnit")} decimals={1} />
              </p>
            </div>
          )}
        </div>
      )}

      {props.children && <div className="doc-calc-body">{props.children}</div>}
    </section>
  );
}
